import { useState, useCallback } from 'react';
import fleetApi from '../../services/api.service';

/**
 * Hook personnalisé pour optimiser la séquence des étapes d'un véhicule
 * @returns {Object} - Objet contenant les états et la fonction d'optimisation
 */
const useOptimizeVehicleSequence = () => {
  // États du hook
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [optimizingVehicles, setOptimizingVehicles] = useState({});

  /**
   * Fonction pour optimiser la séquence des étapes d'un véhicule
   * @param {Object} optimizationParams - Paramètres d'optimisation
   * @param {number} optimizationParams.vehicleId - ID du véhicule
   * @param {string} optimizationParams.vehicleKey - Clé du véhicule
   * @param {Array} optimizationParams.assignedSteps - Étapes assignées au véhicule
   * @param {Array} optimizationParams.currentPosition - Position actuelle [lng, lat]
   * @param {Object} optimizationParams.timeConstraints - Contraintes horaires
   * @param {Object} optimizationParams.optimizationOptions - Options d'optimisation
   * @returns {Promise} - Résultat de l'optimisation
   */
  const optimizeVehicleSequence = useCallback(async (optimizationParams) => {
    const { vehicleId } = optimizationParams;
    
    // Réinitialiser les états
    setIsSuccess(null);
    setError(null);
    setData(null);
    setIsLoading(true);
    
    // Marquer le véhicule comme en cours d'optimisation
    setOptimizingVehicles(prev => ({ ...prev, [vehicleId]: true }));

    try {
      console.log(`🔄 Optimisation des étapes pour le véhicule ${optimizationParams.vehicleKey}`, optimizationParams.assignedSteps);

      const requestData = {
        vehicleKey: optimizationParams.vehicleKey,
        assignedSteps: optimizationParams.assignedSteps,
        currentPosition: optimizationParams.currentPosition,
        timeConstraints: optimizationParams.timeConstraints,
        optimizationOptions: optimizationParams.optimizationOptions
      };

      const response = await fleetApi.post(`/tours-planning/vehicles/${vehicleId}/optimize-sequence`, requestData);

      if (response.status === 200 && response.data) {
        const payload = response.data?.data ?? response.data;
        const improvements = payload.improvements || {};
        const optimizedSequence = payload.optimizedSequence || [];

        console.log(`✅ Optimisation réussie pour le véhicule ${optimizationParams.vehicleKey}:`, {
          totalTimeReductionMinutes: improvements.totalTimeReduction,
          totalDistanceReductionKm: improvements.totalDistanceReduction,
          totalDistanceOptimizedKm: improvements.totalDistanceKm,
          totalDurationOptimizedMinutes: improvements.totalDurationMinutes,
          sequencePreview: optimizedSequence.map((step) => ({
            originalStepId: step.originalStepId,
            position: step.position,
            estimatedArrival: step.estimatedArrival,
          })),
        });

        setIsSuccess(true);
        setData(payload);
        setError(null);
        
        return payload;
      } else {
        throw new Error('Réponse invalide du serveur');
      }
    } catch (err) {
      console.error(`❌ Erreur lors de l'optimisation du véhicule ${vehicleId}:`, err);
      
      const errorMessage = err.response?.data?.message || err.message || `Impossible d'optimiser le véhicule ${optimizationParams.vehicleKey}`;
      setError(errorMessage);
      setIsSuccess(false);
      
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
      // Retirer le véhicule de la liste des véhicules en optimisation
      setOptimizingVehicles(prev => ({ ...prev, [vehicleId]: false }));
    }
  }, []);

  /**
   * Fonction pour vérifier si un véhicule spécifique est en cours d'optimisation
   * @param {number} vehicleId - ID du véhicule
   * @returns {boolean} - True si le véhicule est en cours d'optimisation
   */
  const isVehicleOptimizing = useCallback((vehicleId) => {
    return optimizingVehicles[vehicleId] || false;
  }, [optimizingVehicles]);

  /**
   * Fonction pour réinitialiser tous les états du hook
   */
  const resetStates = useCallback(() => {
    setIsSuccess(null);
    setIsLoading(false);
    setError(null);
    setData(null);
    setOptimizingVehicles({});
  }, []);

  return {
    // États
    isLoading,
    isSuccess,
    error,
    data,
    optimizingVehicles,
    
    // Fonctions
    optimizeVehicleSequence,
    isVehicleOptimizing,
    resetStates,
  };
};

export default useOptimizeVehicleSequence;

